import { useContext, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { ServiceResponse } from '../../../app/services/shared/interfaces';
import useChatService from '../../../app/services/hooks/useChatService';
import useHandleErrorFields from '../../../hooks/useHandleErrorFields';
import ChatDocumentForm from './forms/ChatDocumentForm';
import { AppContext } from '../../../contexts/AppContext';
import LazyLoading from '../../../components/LazyLoading';

interface Props {
    documentId: number;
    onSaved: () => void;
    onCancel: () => void;
    onError: () => void;
}

const ChatDocumentEdit = ({ documentId, onSaved, onCancel, onError }: Props) => {
    const { showLoading, hideLoading } = useContext(AppContext);
    const { showDocument, updateDocument, fetchingShowDocument, fetchingUpdateDocument } = useChatService();
    const { fieldErrorMessage, onFocusRemove, setErrors } = useHandleErrorFields();
    
    const [form, setForm] = useState({
        name: '',
        description: '',
        category: 'other',
        file: null as File | null
    });
    const [currentFileName, setCurrentFileName] = useState<string>('');

    useEffect(() => {
        showDocument(documentId, {
            onSuccess: (response: ServiceResponse) => {
                const doc = response.data.document;
                setForm({
                    name: doc.name || '',
                    description: doc.description || '',
                    category: doc.category || 'other',
                    file: null
                });
                setCurrentFileName(doc.file?.split('/').pop() || '');
            },
            onError: (response: ServiceResponse) => {
                toast.error(response.message);
                onError();
            }
        });
    }, [documentId]);

    const handleInputChange = (field: string) => (value: any) => {
        setForm({ ...form, [field]: value });
    };

    const handleFileChange = (file: File | null) => {
        setForm({ ...form, file });
    };

    const handleSubmit = () => {
        const formData = new FormData();
        formData.append('name', form.name);
        formData.append('description', form.description);
        formData.append('category', form.category);
        if (form.file) {
            formData.append('file', form.file);
        }

        if (showLoading) showLoading('loading', 'Actualizando documento...');

        updateDocument(documentId, formData, {
            onSuccess: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();
                toast.success(response.message);
                setErrors({});
                onSaved();
            },
            onError: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();
                toast.error(response.message);
            },
            onFieldError: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();
                setErrors(response.data);
                toast.error('Por favor corrige los errores en el formulario.');
            }
        });
    };

    if (fetchingShowDocument) {
        return <LazyLoading height={200} />;
    }

    return (
        <ChatDocumentForm
            form={form}
            handleInputChange={handleInputChange}
            handleFileChange={handleFileChange}
            handleSubmit={handleSubmit}
            onCancel={onCancel}
            loading={fetchingUpdateDocument}
            fieldErrorMessage={fieldErrorMessage}
            onFocusRemove={onFocusRemove}
            isEdit={true}
            currentFileName={currentFileName}
        />
    );
};

export default ChatDocumentEdit;

